import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import * as yup from 'yup';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import i18n from '@/i18n/i18n';
import { cn } from '@/lib/utils';
import { CustomIntegration } from '@/shared/types/integration';
import { createYupResolver } from '@/util/forms/createYupResolver';

interface CustomConfigurationProps {
  integration: CustomIntegration | undefined;
  canWrite?: boolean;
}

type CustomConfigFormData = { webhook_url: string; webhook_secret: string };

/**
 * Generates a cryptographically secure random token
 */
const generateToken = (length = 32): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join('');
};

const customConfigSchema = yup
  .object({
    webhook_url: yup
      .string()
      .trim()
      .url(() => i18n.t('validation.invalid_url'))
      .required(() => i18n.t('common.required_field'))
      .defined(),
    webhook_secret: yup.string().trim().max(256).defined(),
  })
  .required();

export const CustomConfiguration = ({ integration, canWrite = true }: CustomConfigurationProps) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { integrationId } = useParams();
  const { updateIntegration, updateStatus } = useIntegrations<CustomIntegration>(
    deskId,
    integrationId
  );

  const isNewIntegration = !integration?.config?.webhook_url;

  const form = useForm<CustomConfigFormData>({
    resolver: createYupResolver<CustomConfigFormData>(customConfigSchema),
    defaultValues: {
      webhook_url: integration?.config?.webhook_url || '',
      webhook_secret: integration?.config?.webhook_secret || '',
    },
    mode: 'onSubmit',
  });

  const {
    formState: { isSubmitting, isDirty },
    setValue,
  } = form;

  const handleGenerateSecret = () => {
    const token = generateToken(32);
    setValue('webhook_secret', token, { shouldValidate: true, shouldDirty: true });
    toast.success(t('integrations.custom.secret_generated'));
  };

  const onValidSubmit = (data: CustomConfigFormData) => {
    updateIntegration({
      ...integration,
      config: {
        ...integration?.config,
        webhook_url: data.webhook_url,
        webhook_secret: data.webhook_secret || undefined,
      },
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="px-1.5 text-sm leading-none font-medium text-muted-foreground">
        {t('integrations.custom.webhook_settings')}
      </p>
      <Card className="py-0 shadow-none">
        <CardContent className="px-4 pt-4 pb-5">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onValidSubmit)} className="flex flex-col gap-4">
              {/* Webhook URL Field */}
              <FormField
                control={form.control}
                name="webhook_url"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>{t('integrations.custom.webhook_url')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('integrations.custom.webhook_url_placeholder')}
                        disabled={!canWrite}
                        autoFocus={isNewIntegration && canWrite}
                      />
                    </FormControl>
                    <FormDescription>{t('integrations.custom.webhook_url_help')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Webhook Secret Field with Generate Button */}
              <FormField
                control={form.control}
                name="webhook_secret"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>{t('integrations.custom.webhook_secret')}</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t('integrations.custom.webhook_secret_placeholder')}
                          disabled={!canWrite}
                        />
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline-neutral"
                        onClick={handleGenerateSecret}
                        disabled={!canWrite}
                        className="shrink-0"
                      >
                        {t('integrations.custom.generate_secret')}
                      </Button>
                    </div>
                    <FormDescription>
                      {t('integrations.custom.webhook_secret_help')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Animated Save Button */}
              <div
                className={cn(
                  'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
                  isDirty ? 'grid-rows-expanded opacity-100' : 'grid-rows-collapsed opacity-0'
                )}
              >
                <div className="overflow-hidden">
                  <div className="flex justify-end pt-2">
                    <Button
                      type="submit"
                      disabled={!canWrite}
                      isLoading={updateStatus === 'pending' || isSubmitting}
                    >
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
